import React, {memo} from 'react';

import cs from 'classnames';
import filter from 'lodash/filter';
import {Link} from 'react-router';
import styled from 'styled-components';

import {DocumentActionsButton} from 'app/components/general/DocumentActionsButton';
import {ItemRelationsConnection} from 'app/folders/components/ItemRelationsConnection';
import {
  StyledCompactItemHeaderDiv,
  StyledCompactItemHeaderSpan,
} from 'app/general/bootstrap/utils';
import {DocIsDraftBadge} from 'app/general/dashboards/DocIsDraftBadge';
import {DocIsTemplateBadge} from 'app/general/dashboards/DocIsTemplateBadge';
import {ItemLatestActivity} from 'app/general/dashboards/ItemLastActivity';
import {ItemRelationsBadges} from 'app/general/dashboards/ItemRelationsBadges';
import {ItemRelationsListOfGenerics} from 'app/general/dashboards/ItemRelationsListOfGenerics';
import {ItemRelationsListOfTeams} from 'app/general/dashboards/ItemRelationsListOfTeams';
import {ItemRelationsLists} from 'app/general/dashboards/ItemRelationsLists';
import {
  DocumentPrivateStatusIcon,
  DocumentSequencedStepsStatusIcon,
} from 'app/general/doc-status';
import {CheckboxButton} from 'app/general/widgets/CheckboxButton';
import {DocumentIconWithNotifications} from 'app/notifly/components/DocumentIconWithNotifications';
import {DashboardItemPresentMembers} from 'app/presence/components/DashboardItemPresentMembers';
import {getContent} from 'app/procedures/utils';
import {DocNeedsReviewBadge} from 'app/review/components/DocNeedReviewBadge';
import {getProcedureName} from 'app/utils/fallbacks';
import {deepDiffer} from 'app/utils/react-utils';

import {StyledDocItem} from '../styles';

import type {BadgeRelation} from 'app/general/dashboards/ItemRelationsBadges';
import type {NotificationType} from 'app/notifly/types';
import type {
  ConnectionFieldSerializedType,
  ContentType,
  ProcedureSerializedType,
  RawProcedureSerializedType,
} from 'sweet-types';

type CompactProps = {
  refreshData: () => void;
  object: ProcedureSerializedType;
  ordering?: string;
  activeRelations?: Record<ContentType, boolean>;
  showRelations?: boolean;
  toggleProceduresDashboardRelation?: (
    type: ContentType,
    activeState: boolean
  ) => void;
  notifications?: Array<NotificationType>;
  enableBulkSelect: boolean;
  isSelected: boolean;
  addToSelection: (procedure: ProcedureSerializedType) => unknown;
  removeFromSelection: (procedure: ProcedureSerializedType) => unknown;
};

const StyledItemDiv = styled(StyledDocItem)`
  position: relative;
`;

const relations: Array<BadgeRelation> = [
  {
    ct: 'tag',
    getter: (p: RawProcedureSerializedType) => p.tags,
    Component: ItemRelationsListOfGenerics,
  },
  {
    ct: 'team',
    getter: (p: RawProcedureSerializedType) =>
      Array.from(new Set(p.team_memberships.map((team) => team.team))),
    Component: ItemRelationsListOfTeams,
  },
  {
    ct: 'proceduregroup',
    getter: (p: RawProcedureSerializedType) => p.processes || [],
    Component: ItemRelationsListOfGenerics,
  },
  {
    ct: 'decision',
    getter: (p: RawProcedureSerializedType) => {
      return getContent(p.content)
        .filter((content) => content.type === 'decision')
        .map((content) => ({
          ...content,
          name: content.title,
        }));
    },
    Component: ItemRelationsListOfGenerics,
  },
  {
    ct: 'policy',
    getter: (p: ProcedureSerializedType) => p.policies || [],
    Component: ItemRelationsListOfGenerics,
  },
  {
    ct: 'folder',
    getter: (p: ProcedureSerializedType) => {
      const connections = p.connections;
      return filter(
        connections,
        (connection: ConnectionFieldSerializedType) => {
          return (
            connection.team == null &&
            connection.process == null &&
            connection.folder != null
          );
        }
      );
    },
    Component: ItemRelationsConnection,
  },
];

export const CompactProcedureListItem = memo(
  ({
    object,
    refreshData,
    ordering,
    showRelations,
    activeRelations,
    toggleProceduresDashboardRelation,
    notifications,
    enableBulkSelect,
    isSelected,
    addToSelection,
    removeFromSelection,
  }: CompactProps) => (
    <StyledItemDiv
      className={cs('row border-bottom py-2 d-flex align-items-center', {
        'opacity-overlay': object.approved_at == null,
      })}
    >
      <DashboardItemPresentMembers
        contentType="procedure"
        objectId={object.id}
      />

      <StyledCompactItemHeaderDiv>
        {enableBulkSelect && (
          <CheckboxButton
            isChecked={isSelected}
            onClick={(isChecked: boolean) =>
              isChecked ? addToSelection(object) : removeFromSelection(object)
            }
            useRealCheckbox={true}
            className="mr-3 btn btn-sm btn-link"
          />
        )}
        <Link
          to={object.html_url}
          className="text-dark font-weight-bold d-block d-sm-inline"
        >
          <StyledCompactItemHeaderSpan>
            <DocumentIconWithNotifications
              contentType="procedure"
              notifications={notifications}
            />{' '}
            {getProcedureName(object, true)}
            <DocIsDraftBadge approved_at={object.approved_at} />
            {object.next_review_at != null ? (
              <DocNeedsReviewBadge reviewDate={object.next_review_at} />
            ) : null}
          </StyledCompactItemHeaderSpan>
        </Link>
        <DocIsTemplateBadge template_at={object.template_at} />
      </StyledCompactItemHeaderDiv>

      {showRelations === true && toggleProceduresDashboardRelation != null ? (
        <ItemRelationsBadges
          className="col-auto ml-auto ml-sm-0 order-lg-3"
          relations={relations}
          object={object}
          activeRelations={activeRelations}
          onToggle={toggleProceduresDashboardRelation}
        />
      ) : null}

      <div className="col-10 col-sm-6 col-lg-3 mr-auto order-lg-4">
        <ItemLatestActivity object={object} sortBy={ordering} />
      </div>

      {object.steps_blocked_at != null && (
        <div className="pl-0 col-2 col-sm-auto text-right col-lg-auto order-lg-2">
          <DocumentSequencedStepsStatusIcon
            isSequenced={object.steps_blocked_at != null}
            contentType="procedure"
          />
        </div>
      )}

      {object.private !== undefined && !object.private ? (
        <div className="pl-0 col-2 col-sm-auto text-right col-lg-auto order-lg-2">
          <DocumentPrivateStatusIcon isPrivate={false} ct="procedure" />
        </div>
      ) : null}

      <div className="col-auto order-lg-5">
        <DocumentActionsButton
          document={object}
          pageContext="dashboard"
          refreshData={refreshData}
        />
      </div>

      {showRelations === true &&
      Object.keys(activeRelations || {}).length > 0 ? (
        <div className="col-12 order-last">
          <ItemRelationsLists
            object={object}
            activeRelations={activeRelations}
            relations={relations}
          />
        </div>
      ) : null}
    </StyledItemDiv>
  ),
  (props, nextProps) => !deepDiffer(props, nextProps)
);
